<!--
  Brand book / design system for microscope.
  Source of truth for how @staff/ui components look and how they compose.
  Reachable only via /design-system (not linked from any nav).

  Styling follows DESIGN_SYSTEM_MICROSCOPE.md: Tailwind for layout/spacing only,
  colours/borders/radius via var(--color-*) tokens in the scoped block below.
-->
<template>
  <div class="ds min-h-screen">
    <div class="ds-container mx-auto flex flex-col gap-8 px-6 py-10">
      <header class="flex flex-col gap-2">
        <h1>Designsystem</h1>
        <p class="text-subtle max-w-2xl">
          Hur våra delade komponenter (<code>@staff/ui</code>) ser ut och används. Färger, spacing och layout kommer
          från design-tokens i <code>theme.scss</code>. Sidan är inte länkad någonstans — den är bara till för dig och
          utvecklare.
        </p>
      </header>

      <!-- In-page table of contents (anchors within this page, not nav links) -->
      <nav class="ds-toc flex flex-wrap gap-2" aria-label="Innehåll">
        <a v-for="section in sections" :key="section.id" class="ds-toc-link" :href="`#${section.id}`">
          {{ section.label }}
        </a>
      </nav>

      <!-- COLOURS -->
      <SectionCard id="colors" title="Färger" subtitle="semantiska roll-tokens">
        <div class="flex flex-col gap-6">
          <div v-for="group in colorGroups" :key="group.title" class="flex flex-col gap-2">
            <h4>{{ group.title }}</h4>
            <div class="flex flex-wrap gap-3">
              <div v-for="token in group.tokens" :key="token" class="ds-swatch">
                <span class="ds-swatch-chip" :style="{ backgroundColor: `var(${token})` }" />
                <code class="ds-swatch-label">{{ token }}</code>
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      <!-- BUTTONS -->
      <SectionCard id="buttons" title="Knappar" subtitle="AppButton">
        <div class="flex flex-col">
          <div class="ds-example">
            <span class="ds-example-label">Färger</span>
            <div class="flex flex-wrap items-center gap-3">
              <AppButton medium>Default</AppButton>
              <AppButton color="blue" medium>Blue</AppButton>
              <AppButton color="green" medium>Green</AppButton>
              <AppButton color="red" medium>Red</AppButton>
              <AppButton color="transparent" medium>Transparent</AppButton>
            </div>
          </div>

          <div class="ds-example">
            <span class="ds-example-label">Storlekar</span>
            <div class="flex flex-wrap items-center gap-3">
              <AppButton color="blue" small>Small</AppButton>
              <AppButton color="blue" medium>Medium</AppButton>
              <AppButton color="blue" large>Large</AppButton>
            </div>
          </div>

          <div class="ds-example">
            <span class="ds-example-label">Tillstånd</span>
            <div class="flex flex-wrap items-center gap-3">
              <AppButton color="blue" medium>Normal</AppButton>
              <AppButton color="blue" medium disabled>Disabled</AppButton>
              <AppButton color="blue" medium loading>Loading</AppButton>
            </div>
          </div>

          <div class="ds-example">
            <span class="ds-example-label">Med ikon / icon-only / block</span>
            <div class="flex flex-wrap items-center gap-3">
              <AppButton color="blue" medium>
                <template #prepend><SvgIcon :path="mdiPlus" :size="18" /></template>
                Ny ansökan
              </AppButton>
              <AppButton icon small aria-label="Sök">
                <SvgIcon :path="mdiMagnify" :size="18" />
              </AppButton>
              <AppButton icon small aria-label="Redigera">
                <SvgIcon :path="mdiPencil" :size="18" />
              </AppButton>
            </div>
            <div class="mt-3 max-w-sm">
              <AppButton color="blue" medium block>Block (full bredd)</AppButton>
            </div>
          </div>
        </div>
      </SectionCard>

      <!-- CHIPS & BADGES -->
      <SectionCard id="chips" title="Chips & badges" subtitle="AppChip, AppFilterChip, AppNotificationBadge">
        <div class="flex flex-col">
          <div class="ds-example">
            <span class="ds-example-label">AppChip</span>
            <div class="flex flex-wrap items-center gap-3">
              <AppChip v-for="color in chipColors" :key="color" :color="color">{{ color }}</AppChip>
            </div>
            <div class="mt-3 flex flex-wrap items-center gap-3">
              <AppChip v-for="color in chipColors" :key="`dot-${color}`" :color="color" dot>{{ color }}</AppChip>
              <AppChip v-for="color in chipColors" :key="`outlined-${color}`" :color="color" outlined>{{
                color
              }}</AppChip>
            </div>
          </div>

          <div class="ds-example">
            <span class="ds-example-label">AppFilterChip</span>
            <div class="flex flex-wrap items-center gap-3">
              <AppFilterChip v-model="filterChipOn">På</AppFilterChip>
              <AppFilterChip>Av</AppFilterChip>
            </div>
          </div>

          <div class="ds-example">
            <span class="ds-example-label"
              >AppNotificationBadge (i AppButtons #badge-slot, som i OpenConversationsButton)</span
            >
            <div class="flex flex-wrap items-center gap-6">
              <AppButton color="blue" medium>
                Meddelanden
                <template #badge><AppNotificationBadge :count="3" /></template>
              </AppButton>
              <AppButton color="blue" medium>
                Meddelanden
                <template #badge><AppNotificationBadge :count="150" /></template>
              </AppButton>
              <AppButton color="blue" medium>
                Meddelanden
                <template #badge><AppNotificationBadge :count="3" color="brand" /></template>
              </AppButton>
              <AppButton color="blue" medium>
                Aviseringar
                <template #badge><AppNotificationBadge dot /></template>
              </AppButton>
            </div>
          </div>
        </div>
      </SectionCard>

      <!-- ALERTS -->
      <SectionCard id="feedback" title="Alerts" subtitle="notify() → global AppAlert">
        <div class="ds-example">
          <span class="ds-example-label">Trigga en notis (visas uppe till höger)</span>
          <div class="flex flex-wrap items-center gap-3">
            <AppButton color="green" medium @click="triggerAlert('success')">Success</AppButton>
            <AppButton color="red" medium @click="triggerAlert('error')">Error</AppButton>
            <AppButton medium @click="triggerAlert('warning')">Warning</AppButton>
            <AppButton color="blue" medium @click="triggerAlert('info')">Info</AppButton>
          </div>
        </div>
      </SectionCard>

      <!-- DECISIONS AND STEPS -->
      <SectionCard id="decisions" title="Beslut & steg" subtitle="AppDecisionForm, AppStepTrack">
        <div class="ds-example">
          <span class="ds-example-label">AppDecisionForm: knappen först, sedan kommentar och bekräftelse</span>
          <AppDecisionForm :options="decisionOptions" id-prefix="ds-decision" @submit="onDecisionSubmit" />
        </div>
        <div class="ds-example">
          <span class="ds-example-label">AppStepTrack: passerat, nu, överhoppat och kvar</span>
          <AppStepTrack :steps="trackSteps" />
        </div>
      </SectionCard>

      <!-- MODAL -->
      <SectionCard id="modal" title="Modal" subtitle="AppModal via useModal()-composablen">
        <div class="ds-example">
          <span class="ds-example-label">
            Modaler triggas med openModal(titel, komponent, props, options) — den globala AppModal i App.vue renderar
            dem
          </span>
          <div class="flex flex-wrap items-center gap-3">
            <AppButton color="blue" medium @click="openFormModal('Lägg till personal', 'default')"
              >Öppna modal</AppButton
            >
            <AppButton color="blue" medium @click="openFormModal('Uppdatera roller', 'large')"
              >Öppna stor modal</AppButton
            >
          </div>
        </div>
      </SectionCard>

      <!-- INPUTS -->
      <SectionCard
        id="inputs"
        title="Formulärfält"
        subtitle="AppTextField, AppTextArea, AppSelect, AppCheckbox, AppRadioGroup, CountryIdList"
      >
        <p class="text-subtle mb-4">
          Normalt används bara <strong>default</strong> och <strong>error</strong>. <strong>success</strong> och
          <strong>warning</strong> är avsedda för specialfall (idag bara i periscope — t.ex. success för "värdet har
          ändrats" och warning för en enskild flagga).
        </p>
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <AppTextField v-model="textValue" label="Textfält" placeholder="Skriv något" hint="En hjälptext" />
          <AppTextField v-model="amountValue" type="amount" label="Belopp" suffix="kr" placeholder="0" />
          <AppTextField label="Error" model-value="Fel värde" error :error-messages="['Obligatoriskt fält']" />
          <AppTextField label="Disabled" model-value="Går ej att ändra" disabled />
          <AppTextField
            label="Success (specialfall)"
            model-value="Godkänt värde"
            success
            :success-messages="['Ser bra ut']"
          />
          <AppTextField
            label="Warning (specialfall)"
            model-value="Kontrollera"
            warning
            :warning-messages="['Dubbelkolla detta']"
          />
          <AppSelect v-model="selectValue" label="Select" :options="selectOptions" placeholder="Välj ett alternativ" />
          <AppTextArea v-model="areaValue" label="Textarea" placeholder="Längre text..." :rows="3" />
          <div class="flex flex-col gap-3">
            <AppCheckbox v-model="checkedValue" label="Kryssruta" />
            <AppRadioGroup v-model="radioValue" label="Radio (rad)" :options="radioOptions" direction="row" />
            <AppRadioGroup v-model="radioValue" label="Radio (kolumn)" :options="radioOptions" direction="column" />
          </div>
        </div>

        <div class="ds-example">
          <span class="ds-example-label"
            >Validering (AppValidationGroup + rules från @staff/shared) — klicka Spara med tomma fält</span
          >
          <AppValidationGroup v-slot="{ validate }">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <AppTextField v-model="validationName" label="Namn" placeholder="Anna" :rules="[required()]" />
              <AppTextField
                v-model="validationEmail"
                label="E-post"
                placeholder="anna@exempel.se"
                :rules="[required(), email()]"
              />
            </div>
            <div class="mt-4 flex justify-end">
              <AppButton color="blue" small @click="validate(() => triggerAlert('success'))">Spara</AppButton>
            </div>
          </AppValidationGroup>
        </div>

        <div class="ds-example">
          <span class="ds-example-label">AppDocumentUpload — fil-uppladdning (drag/drop + fillista)</span>
          <AppDocumentUpload v-model="uploadFiles" />
        </div>

        <div class="ds-example">
          <span class="ds-example-label"
            >CountryIdList — länder med ett ID-nummer per land (1–5, Sverige förvalt, ett primärt). Används i
            KYC-formuläret — klicka Kontrollera med tomma ID-nummer</span
          >
          <AppValidationGroup v-slot="{ validate }">
            <div class="ds-country-id-example">
              <div class="ds-country-id-lists">
                <CountryIdList
                  v-model="citizenships"
                  variant="citizenship"
                  label="Medborgarskap"
                  id-label="ID-nummer"
                  add-label="Lägg till medborgarskap"
                />
                <CountryIdList
                  v-model="taxDomiciles"
                  variant="taxDomicile"
                  label="Skatterättslig hemvist"
                  id-label="TIN"
                  add-label="Lägg till skatterättslig hemvist"
                />
              </div>
              <div class="ds-country-id-actions">
                <AppButton color="blue" small @click="validate(() => triggerAlert('success'))">Kontrollera</AppButton>
              </div>
            </div>
          </AppValidationGroup>
        </div>
      </SectionCard>

      <!-- CONTAINERS -->
      <SectionCard id="containers" title="Kort & sektioner" subtitle="AppCard, SectionCard">
        <p class="text-subtle mb-4">
          AppCard är en ren container (ingen egen header). Den wrappar innehåll — data, formulär eller listrader.
          Behöver du en rubriksatt sektion, använd SectionCard. (AppTable är redan självständig med egen ram — lägg den
          inte i en AppCard.)
        </p>
        <div class="ds-grid grid grid-cols-1 gap-6 md:grid-cols-2">
          <div class="ds-example">
            <span class="ds-example-label">AppCard (default)</span>
            <AppCard>
              <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between gap-4">
                  <span class="text-subtle">Belopp</span><span class="font-medium">2 500 000 kr</span>
                </div>
                <div class="flex items-center justify-between gap-4">
                  <span class="text-subtle">Ränta</span><span class="font-medium">3,45 %</span>
                </div>
                <div class="flex items-center justify-between gap-4">
                  <span class="text-subtle">Status</span><AppChip color="success">Aktiv</AppChip>
                </div>
              </div>
            </AppCard>
          </div>

          <div class="ds-example">
            <span class="ds-example-label">AppCard inset (infälld yta)</span>
            <AppCard inset>
              <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between gap-4">
                  <span class="text-subtle">Belopp</span><span class="font-medium">2 500 000 kr</span>
                </div>
                <div class="flex items-center justify-between gap-4">
                  <span class="text-subtle">Ränta</span><span class="font-medium">3,45 %</span>
                </div>
              </div>
            </AppCard>
          </div>

          <div class="ds-example">
            <span class="ds-example-label">AppCard no-padding (innehåll kant-i-kant, t.ex. listrader)</span>
            <AppCard no-padding>
              <div class="ds-list-row flex items-center justify-between gap-4 px-4 py-3">
                <span>Anna Andersson</span><AppChip color="success">Aktiv</AppChip>
              </div>
              <div class="ds-list-row flex items-center justify-between gap-4 px-4 py-3">
                <span>Björn Berg</span><AppChip color="warning">Väntar</AppChip>
              </div>
            </AppCard>
          </div>

          <div class="ds-example">
            <span class="ds-example-label">AppCard loading (stapel i överkanten medan innehåll laddar)</span>
            <AppCard loading>
              <div class="flex flex-col gap-2">
                <div class="flex items-center justify-between gap-4">
                  <span class="text-subtle">Belopp</span><span class="font-medium">2 500 000 kr</span>
                </div>
                <div class="flex items-center justify-between gap-4">
                  <span class="text-subtle">Ränta</span><span class="font-medium">3,45 %</span>
                </div>
              </div>
            </AppCard>
          </div>
        </div>

        <div class="ds-grid mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div class="ds-example">
            <span class="ds-example-label">SectionCard (egen header, till skillnad från AppCard)</span>
            <SectionCard title="Exempelsektion" subtitle="med undertitel" heading-tag="h3">
              <p class="text-subtle">SectionCard används för de rubriksatta blocken på den här sidan.</p>
            </SectionCard>
          </div>
          <div class="ds-example">
            <span class="ds-example-label">SectionCard loading (stapel under headern)</span>
            <SectionCard title="Hämtar data" heading-tag="h3" loading>
              <p class="text-subtle">Innehållet visas medan sektionen laddar.</p>
            </SectionCard>
          </div>
        </div>
      </SectionCard>

      <!-- TABS -->
      <SectionCard id="navigation" title="Tabs" subtitle="AppTabs med nav-länkar (AppRouterLink variant='nav')">
        <div class="ds-example">
          <span class="ds-example-label">#left / #right-slots, aktiv flik markerad</span>
          <AppTabs>
            <template #left>
              <AppRouterLink to="/design-system" variant="nav" class="nav-link nav-link-active font-semibold">
                Översikt
              </AppRouterLink>
            </template>
            <template #right>
              <AppRouterLink to="/design-system" variant="nav" class="nav-link">Dokument</AppRouterLink>
              <AppRouterLink to="/design-system" variant="nav" class="nav-link">Kompletteringar</AppRouterLink>
            </template>
          </AppTabs>
        </div>

        <div class="ds-example">
          <span class="ds-example-label">:is-loading — progress-linjal under flikarna</span>
          <AppTabs :is-loading="true">
            <template #left>
              <AppRouterLink to="/design-system" variant="nav" class="nav-link nav-link-active font-semibold">
                Översikt
              </AppRouterLink>
            </template>
            <template #right>
              <AppRouterLink to="/design-system" variant="nav" class="nav-link">Dokument</AppRouterLink>
              <AppRouterLink to="/design-system" variant="nav" class="nav-link">Kompletteringar</AppRouterLink>
            </template>
          </AppTabs>
        </div>
      </SectionCard>

      <!-- MENU & DATE -->
      <SectionCard id="menu" title="Meny & datumväljare" subtitle="AppMenu, AppDatePicker">
        <div class="ds-example">
          <span class="ds-example-label">AppMenu — dropdown (activator + innehåll via slots)</span>
          <AppMenu>
            <template #activator="{ open }">
              <AppButton color="blue" medium aria-haspopup="menu" @click="open">
                Åtgärder
                <template #append><SvgIcon :path="mdiChevronDown" :size="16" /></template>
              </AppButton>
            </template>
            <template #default="{ close }">
              <div class="ds-menu" role="menu">
                <button type="button" class="ds-menu-item" role="menuitem" @click="close">Redigera</button>
                <button type="button" class="ds-menu-item" role="menuitem" @click="close">Duplicera</button>
                <button type="button" class="ds-menu-item" role="menuitem" @click="close">Ta bort</button>
              </div>
            </template>
          </AppMenu>
        </div>

        <div class="ds-example">
          <span class="ds-example-label"
            >AppDatePicker i en AppMenu (fält-activator + kalender), som i property-sales</span
          >
          <div class="max-w-xs">
            <AppMenu>
              <template #activator="{ open }">
                <AppTextField
                  v-model="dateValue"
                  type="date"
                  input-mode="numeric"
                  placeholder="ÅÅÅÅ-MM-DD"
                  label="Datum"
                  @focus="open"
                />
              </template>
              <template #default="{ close }">
                <AppDatePicker v-model="dateValue" @input="close" />
              </template>
            </AppMenu>
          </div>
        </div>
      </SectionCard>

      <!-- TABLE -->
      <SectionCard id="table" title="Tabell" subtitle="AppTable + sorterbar header + pagination">
        <p class="text-subtle mb-4">
          AppTable har egen ram och radhover. Sorterbara kolumner använder AppTableSortableHeaderCell, och
          AppTablePaginationFooter läggs direkt under tabellen. Rader kan göras klickbara med
          <code>:route-location</code>.
        </p>
        <AppTable>
          <thead>
            <tr>
              <AppTableSortableHeaderCell :field="'submitted_at'" :sort="tableSort">Inkom</AppTableSortableHeaderCell>
              <th scope="col">Namn</th>
              <th scope="col">Roll</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow v-for="row in tableRows" :key="row.name" :route-location="{ path: '/design-system' }">
              <AppTableCell nowrap>2026-08-01</AppTableCell>
              <AppTableCell>{{ row.name }}</AppTableCell>
              <AppTableCell>{{ row.role }}</AppTableCell>
              <AppTableCell>
                <AppChip :color="row.chip" dot>{{ row.status }}</AppChip>
              </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
        <AppTablePaginationFooter :pagination="tablePagination" />
      </SectionCard>

      <!-- SPACING -->
      <SectionCard id="spacing" title="Spacing" subtitle="skalan (1 = 4px) — spacea med gap, inte margin">
        <p class="text-subtle mb-4">
          Space:a element med förälderns <code>gap</code> (flex/grid), inte margin. Använd skalan — hitta inte på egna
          värden. Komponenters egen padding (AppCard, inputs …) äger komponenten; lägg inte till egen.
        </p>
        <div class="flex flex-col gap-3">
          <div v-for="s in spacingScale" :key="s.step" class="flex items-center gap-4">
            <code class="ds-spacing-name">gap-{{ s.step }}</code>
            <span class="ds-spacing-bar" :style="{ width: `${s.step * 4}px` }" />
            <span class="ds-spacing-px text-subtle text-xs">{{ s.step * 4 }}px</span>
            <span v-if="s.use" class="text-subtle text-xs">{{ s.use }}</span>
          </div>
        </div>
      </SectionCard>

      <!-- RESPONSIVE -->
      <SectionCard
        id="responsive"
        title="Responsivitet"
        subtitle="container-queries för komponenter, viewport för sidramen"
      >
        <p class="text-subtle mb-4">
          Vi tar hänsyn till skärmar från 13" och uppåt (laptops och externa skärmar) — inte telefon. Innehållet kapas
          till max 1440px i AppShell (sidebaren står kvar 250px), så det inte blir utdraget på breda skärmar. En
          komponents egen 1→N-kolumnslayout styrs med CSS <code>@container</code> (anpassar sig efter komponentens yta,
          inte fönstret) — själva sidramen styrs med viewport-breakpoints.
        </p>
        <div class="ds-example">
          <span class="ds-example-label">
            Container-query — griden går 1→2 kolumner efter <em>kortets</em> bredd (ändra fönsterbredden och se)
          </span>
          <div class="rq-demo">
            <div class="rq-grid">
              <div class="rq-cell">1</div>
              <div class="rq-cell">2</div>
              <div class="rq-cell">3</div>
              <div class="rq-cell">4</div>
            </div>
          </div>
        </div>
      </SectionCard>

      <!-- COMPOSITIONS -->
      <SectionCard id="compositions" title="Layout-exempel" subtitle="reproducerade från riktiga vyer">
        <div class="flex flex-col">
          <!-- ApplicationHeader (reproduced from microscope/views/application/ApplicationHeader.vue) -->
          <div class="ds-example">
            <span class="ds-example-label">ApplicationHeader — header-kort på brand-subtle</span>
            <div class="application-header rounded-lg p-4">
              <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2>Bolån – Bostadsrätt</h2>
                  <p class="text-subtle">Storgatan 1, 111 22 Stockholm</p>
                </div>
                <div class="flex flex-col items-end gap-2 text-xs text-subtle">
                  <span>Handläggare: Anna Andersson</span>
                  <span>Skapad: 2026-08-01 10:23</span>
                  <span>Inkom: 2026-08-02 09:00</span>
                </div>
              </div>
              <div class="flex items-center gap-6">
                <div class="shrink-0">
                  <p>Status: <span class="font-medium">Inkommen</span></p>
                  <p>Ansökt belopp: <span class="font-medium">2 500 000 kr</span></p>
                </div>
                <div class="flex flex-wrap gap-4">
                  <div class="applicant-box rounded-md px-4 py-3">
                    <span class="text-xs text-subtle">Huvudlåntagare</span>
                    <div class="font-medium capitalize whitespace-nowrap">anna andersson</div>
                  </div>
                  <div class="applicant-box rounded-md px-4 py-3">
                    <span class="text-xs text-subtle">Medlåntagare</span>
                    <div class="font-medium capitalize whitespace-nowrap">björn berg</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- DebtHeader (reproduced from microscope/views/debt/DebtHeader.vue) -->
          <div class="ds-example">
            <span class="ds-example-label">DebtHeader — header-kort på neutral canvas-subtle</span>
            <div class="debt-card rounded-lg p-4">
              <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2>Brf Utsikten, Lgh 1201</h2>
                  <p class="text-subtle">Storgatan 1 Lgh 1201, 111 22 Stockholm</p>
                  <p class="mt-1">Status: <span class="font-medium">Aktiv</span></p>
                </div>
              </div>
              <div class="flex flex-wrap gap-4">
                <div class="debtor-box rounded-md px-4 py-2">
                  <span class="text-sm text-subtle">Huvudlåntagare</span>
                  <div class="grid grid-cols-[auto_1fr] gap-x-4">
                    <span>Anna Andersson</span>
                    <span class="text-sm text-subtle self-center">Telefon: 070-123 45 67</span>
                    <span class="text-sm text-subtle">19850101-0000</span>
                  </div>
                </div>
                <div class="debtor-box rounded-md px-4 py-2">
                  <span class="text-xs text-subtle">Medlåntagare</span>
                  <div>Björn Berg</div>
                  <div class="text-xs text-subtle">19860202-0000</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Overview section (reproduced from overview/.../RepaymentCapacitySection.vue) -->
          <div class="ds-example">
            <span class="ds-example-label">Översikts-sektion — SectionCard med data-panel</span>
            <SectionCard title="Återbetalningsförmåga" heading-tag="h3">
              <div class="applicant-card flex flex-col gap-4 rounded-lg p-4">
                <div class="flex items-center justify-between gap-4">
                  <span class="text-subtle">Månadsinkomst</span><span class="font-medium">45 000 kr</span>
                </div>
                <div class="flex items-center justify-between gap-4">
                  <span class="text-subtle">Boendekostnad</span><span class="font-medium">8 500 kr</span>
                </div>
                <div class="flex items-center justify-between gap-4">
                  <span class="text-subtle">KALP</span><span class="font-medium">12 300 kr</span>
                </div>
              </div>
            </SectionCard>
          </div>

          <!-- Create-application question (reproduced .item-box pattern) -->
          <div class="ds-example">
            <span class="ds-example-label">Skapa-ansökan-fråga — .item-box-mönstret</span>
            <div class="grid gap-3">
              <div class="grid justify-items-start gap-2">
                <h4>Barn</h4>
                <p class="text-subtle">Lägg till barn som bor i hushållet.</p>
                <AppButton type="button" medium>Lägg till barn i hushållet</AppButton>
              </div>
              <div class="grid gap-3 sm:grid-cols-2">
                <div class="item-box grid min-w-0 gap-3 rounded-lg p-3">
                  <div class="flex flex-wrap items-center justify-between gap-3">
                    <h4>Barn 1</h4>
                    <AppButton type="button" color="red" small>Ta bort</AppButton>
                  </div>
                  <AppTextField label="Barn 1 ålder" placeholder="0" type="amount" />
                  <AppRadioGroup v-model="radioValue" label="Vårdnad" :options="radioOptions" direction="row" />
                </div>
              </div>
            </div>
          </div>

          <!-- Document listing (mirrors ApplicationDocumentsView: name + meta + open action) -->
          <div class="ds-example">
            <span class="ds-example-label">Dokumentlista (namn + typ · storlek + öppna-knapp)</span>
            <AppCard no-padding>
              <div
                v-for="item in documentItems"
                :key="item.title"
                class="ds-list-row flex items-center gap-3 px-4 py-3"
              >
                <SvgIcon :path="mdiFileDocumentOutline" :size="20" />
                <div class="flex min-w-0 flex-col">
                  <span class="font-medium">{{ item.title }}</span>
                  <span class="text-subtle text-xs">{{ item.meta }}</span>
                </div>
                <div class="ml-auto">
                  <AppButton
                    icon
                    small
                    :aria-label="`Öppna ${item.title}`"
                    title="Visa"
                    @click="openPlaceholderModal(item.title)"
                  >
                    <SvgIcon :path="mdiMagnify" :size="18" />
                  </AppButton>
                </div>
              </div>
            </AppCard>
          </div>
        </div>
      </SectionCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { mdiMagnify, mdiPencil, mdiPlus, mdiFileDocumentOutline, mdiChevronDown } from '@mdi/js'
import {
  AppButton,
  AppCard,
  AppChip,
  AppFilterChip,
  AppCheckbox,
  AppDecisionForm,
  AppStepTrack,
  AppDatePicker,
  AppDocumentUpload,
  AppMenu,
  AppNotificationBadge,
  AppRadioGroup,
  AppSelect,
  AppTable,
  AppTableCell,
  AppTablePaginationFooter,
  AppTableRow,
  AppTableSortableHeaderCell,
  AppTabs,
  AppTextArea,
  AppTextField,
  AppValidationGroup,
  CountryIdList,
  SectionCard,
  SvgIcon,
  notify,
  useModal,
} from '@staff/ui'
import { email, required } from '@staff/shared'
import type {
  AppTablePaginationModel,
  AppTableSortModel,
  ChipColor,
  CitizenshipEntry,
  DecisionOption,
  NotificationType,
  TaxDomicileEntry,
  TrackStep,
} from '@staff/ui'
import AppRouterLink from '@/components/AppRouterLink.vue'
import ModalPlaceholder from './ModalPlaceholder.vue'
import ModalFormExample from './ModalFormExample.vue'

const textValue = ref('')
const amountValue = ref('')
const areaValue = ref('')
const selectValue = ref('')
const checkedValue = ref(true)
const filterChipOn = ref(true)
const radioValue = ref('yes')
const validationName = ref('')
const validationEmail = ref('')
const dateValue = ref('')
const uploadFiles = ref<File[]>([])
const citizenships = ref<CitizenshipEntry[]>([{ countryCode: 'SE', registrationId: '' }])
const taxDomiciles = ref<TaxDomicileEntry[]>([{ countryCode: 'SE', tin: '', isPrimary: true }])

const decisionOptions: DecisionOption[] = [
  {
    value: 'approve',
    label: 'Godkänn',
    tone: 'approve',
    commentRequired: true,
    commentHint: 'Krävs.',
    confirmation: { title: 'Godkänn?', text: 'Vad beslutet gör, sagt innan det fattas.' },
  },
  {
    value: 'reject',
    label: 'Neka',
    tone: 'reject',
    commentRequired: true,
    commentHint: 'Krävs.',
    confirmation: { title: 'Neka?', text: 'Beslutet är slutgiltigt.' },
  },
  {
    value: 'escalate',
    label: 'Eskalera',
    tone: 'neutral',
    disabledReason: 'Varför knappen inte går att trycka på, och vad man gör i stället.',
    commentRequired: false,
    confirmation: null,
  },
]
const onDecisionSubmit = ({ value }: { value: string }) => notify({ type: 'info', title: `Beslut: ${value}` })

const trackSteps: TrackStep[] = [
  { label: 'Inväntar svar', state: 'done' },
  { label: 'Svar mottagna', state: 'done' },
  { label: 'KYC-granskning', state: 'skipped' },
  { label: 'KYC-granskning hos FCP', state: 'current' },
  { label: 'Beslut', state: 'upcoming' },
]

const selectOptions = [
  { text: 'Alternativ 1', value: '1' },
  { text: 'Alternativ 2', value: '2' },
  { text: 'Alternativ 3', value: '3' },
]

const radioOptions = [
  { text: 'Ja', value: 'yes' },
  { text: 'Nej', value: 'no' },
  { text: 'Kanske', value: 'maybe' },
]

const chipColors: ChipColor[] = ['brand', 'success', 'warning', 'danger', 'muted']

// Static stand-in models so the table showcase renders without a live data source.
const tableSort: AppTableSortModel = { field: 'submitted_at', direction: 'desc', toggle: () => {} }
const tablePagination: AppTablePaginationModel = {
  pageStart: 1,
  pageStop: 3,
  totalItems: 3,
  currentPage: 1,
  totalPages: 1,
  pageSize: 25,
  pageSizeOptions: [25, 50, 100],
  pageSizeText: 'Rader per sida',
  isPreviousPageDisabled: true,
  isNextPageDisabled: true,
  setPageSize: () => {},
  previousPage: () => {},
  nextPage: () => {},
}

const spacingScale: { step: number; use: string }[] = [
  { step: 2, use: 'tight — knapprad, chips, ikon + text' },
  { step: 3, use: 'default litet mellanrum' },
  { step: 4, use: 'formulärfält' },
  { step: 6, use: 'mellan sektioner / block' },
  { step: 8, use: '' },
  { step: 12, use: '' },
]

const colorGroups = [
  {
    title: 'Foreground',
    tokens: ['--color-fg-default', '--color-fg-subtle', '--color-fg-muted', '--color-fg-disabled'],
  },
  {
    title: 'Canvas',
    tokens: ['--color-canvas-default', '--color-canvas-inset', '--color-canvas-muted', '--color-canvas-subtle'],
  },
  { title: 'Border', tokens: ['--color-border-default', '--color-border-muted', '--color-border-divider'] },
  { title: 'Brand', tokens: ['--color-brand-subtle', '--color-brand-default', '--color-brand-emphasis'] },
  {
    title: 'Danger',
    tokens: ['--color-danger-subtle', '--color-danger-fg', '--color-danger-emphasis', '--color-danger-on-subtle'],
  },
  {
    title: 'Warning',
    tokens: ['--color-warning-subtle', '--color-warning-fg', '--color-warning-emphasis', '--color-warning-on-subtle'],
  },
  {
    title: 'Success',
    tokens: ['--color-success-subtle', '--color-success-fg', '--color-success-emphasis', '--color-success-on-subtle'],
  },
]

const tableRows: { name: string; role: string; status: string; chip: ChipColor }[] = [
  { name: 'Anna Andersson', role: 'Handläggare', status: 'Aktiv', chip: 'success' },
  { name: 'Björn Berg', role: 'Personalansvarig', status: 'Väntar', chip: 'warning' },
  { name: 'Cecilia Ceder', role: 'Handläggare', status: 'Inaktiv', chip: 'muted' },
]

const documentItems: { title: string; meta: string }[] = [
  { title: 'Lånehandling.pdf', meta: 'PDF · 240 kB' },
  { title: 'Inkomstunderlag.pdf', meta: 'PDF · 1.2 MB' },
]

const sections = [
  { id: 'colors', label: 'Färger' },
  { id: 'buttons', label: 'Knappar' },
  { id: 'chips', label: 'Chips & badges' },
  { id: 'feedback', label: 'Alerts' },
  { id: 'decisions', label: 'Beslut & steg' },
  { id: 'modal', label: 'Modal' },
  { id: 'inputs', label: 'Formulärfält' },
  { id: 'containers', label: 'Kort & sektioner' },
  { id: 'navigation', label: 'Tabs' },
  { id: 'menu', label: 'Meny & datum' },
  { id: 'table', label: 'Tabell' },
  { id: 'spacing', label: 'Spacing' },
  { id: 'responsive', label: 'Responsivitet' },
  { id: 'compositions', label: 'Layout-exempel' },
]

const triggerAlert = (type: NotificationType) =>
  notify({ type, title: `${type} notis`, description: 'Exempeltext för en notis.' })

const { openModal } = useModal()

// Opens AppModal (mounted globally in App.vue) via the modal composable, the way
// document preview does — but with a placeholder body instead of a real document.
const openPlaceholderModal = (title: string, size: 'default' | 'large' = 'large') =>
  openModal(title, ModalPlaceholder, {}, { size })

// Form-in-a-modal, mirroring the real PersonnelForm modal (add/edit personnel).
const openFormModal = (title: string, size: 'default' | 'large' = 'default') =>
  openModal(title, ModalFormExample, {}, { size })
</script>

<style scoped lang="scss">
.ds {
  background-color: var(--color-canvas-inset);
}

.ds-container {
  max-width: 960px;
}

code {
  font-size: 0.85em;
}

.ds-toc-link {
  border: 1px solid var(--color-border-muted);
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 0.8125rem;
  color: var(--color-fg-subtle);
  background-color: var(--color-canvas-default);

  &:hover {
    color: var(--color-brand-default);
    border-color: var(--color-brand-default);
  }
}

.ds-example:not(:first-child) {
  margin-top: 24px;
}

.ds-grid > .ds-example {
  margin-top: 0;
}

// CountryIdList example: the two lists side by side once there is room for both.
.ds-country-id-example {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
  container-type: inline-size;
}

.ds-country-id-lists {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-24);
}

@container (min-width: 48rem) {
  .ds-country-id-lists {
    grid-template-columns: 1fr 1fr;
  }
}

.ds-country-id-actions {
  display: flex;
  justify-content: flex-end;
}

.ds-example-label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.75rem;
  color: var(--color-fg-subtle);
}

// Colour swatch.
.ds-swatch {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 140px;
}

.ds-swatch-chip {
  height: 44px;
  border-radius: 8px;
  border: 1px solid var(--color-border-divider);
}

.ds-swatch-label {
  font-size: 0.6875rem;
  color: var(--color-fg-subtle);
}

// AppMenu dropdown panel.
.ds-menu {
  min-width: 12rem;
  padding: 4px 0;
  border-radius: 6px;
  border: 1px solid var(--color-border-muted);
  background-color: var(--color-canvas-default);
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.12);
}

.ds-menu-item {
  display: block;
  width: 100%;
  padding: 8px 16px;
  text-align: left;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background-color: var(--color-canvas-hover);
    outline: none;
  }
}

// Nav-link styling for the AppTabs showcase (mirrors ApplicationLayout).
.nav-link:hover {
  background-color: var(--color-button-default-hover-bg);
}

.nav-link-active {
  background-color: var(--color-brand-subtle);
  color: var(--color-brand-default);
}

// Spacing scale.
.ds-spacing-name {
  width: 130px;
  font-size: 0.75rem;
  color: var(--color-fg-subtle);
}

.ds-spacing-bar {
  height: 16px;
  border-radius: 4px;
  background-color: var(--color-brand-default);
}

.ds-spacing-px {
  min-width: 48px;
}

// Container-query demo: the grid reflows on the container's width, not the viewport.
.rq-demo {
  container-type: inline-size;
}

.rq-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@container (min-width: 32rem) {
  .rq-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.rq-cell {
  padding: 16px;
  border: 1px solid var(--color-border-muted);
  border-radius: 6px;
  background-color: var(--color-canvas-inset);
  text-align: center;
}

// List composition.
.ds-list-row + .ds-list-row {
  border-top: 1px solid var(--color-border-divider);
}

// Reproduced real-component surfaces (mirror their scoped styles in the app).
.application-header {
  background-color: var(--color-brand-subtle);
}

.applicant-box {
  border: 1px solid var(--color-border-divider);
  background-color: var(--color-canvas-translucent);
}

.debt-card {
  background-color: var(--color-canvas-subtle);
  border: 1px solid var(--color-border-default);
}

.debtor-box {
  border: 1px solid var(--color-border-muted);
  background-color: var(--color-canvas-translucent);
}

.applicant-card {
  border: 1px solid var(--color-border-muted);
  background-color: var(--color-canvas-inset);
}

.item-box {
  border: 1px solid var(--color-border-muted);
}
</style>
